import { motion, useInView } from 'framer-motion'
import { Img } from 'openimg/react'
import { useRef } from 'react'
import { Link } from 'react-router'
import { InteractiveHoverButton } from '#app/components/ui/interactive-hover-button.tsx'
import { CTA_DEFAULT_FONT_PRESET, CTA_FONT_PRESETS } from '#app/utils/cta-constants.ts'
import {
	CONDENSED_FONT_STYLE,
	renderHighlightedTitle,
} from '#app/utils/text-rendering.tsx'

// Default image asset from Figma - person with tropical shirt
const defaultCtaPersonImage =
	'https://www.figma.com/api/mcp/asset/316defb2-d5d9-436e-95a6-9c335a5ece1f'

interface CtaSamenwerkenProps {
	title?: string | null
	buttonText?: string | null
	buttonLink?: string | null
	imageUrl?: string | null
	imageAlt?: string | null
	highlightWords?: string | null
	fontSizePreset?: string | null
	showButton?: boolean | null
}

export function CtaSamenwerken({
	title,
	buttonText,
	buttonLink,
	imageUrl,
	imageAlt,
	highlightWords,
	fontSizePreset,
	showButton,
}: CtaSamenwerkenProps = {}) {
	const sectionRef = useRef(null)
	const isInView = useInView(sectionRef, {
		once: true,
		margin: '-100px',
		amount: 0.3,
	})

	const displayTitle = title || 'Een razend\nvuurtje voor\njouw merk'
	const displayButtonText = buttonText || 'Samenwerken'
	const displayButtonLink = buttonLink || '/contact'
	const displayImageUrl = imageUrl || defaultCtaPersonImage
	const displayImageAlt = imageAlt || 'Ignite team member'
	const displayShowButton = showButton !== false

	// Resolve font size from preset
	const preset = fontSizePreset || CTA_DEFAULT_FONT_PRESET
	const fontSizes: [number, number, number] =
		CTA_FONT_PRESETS[preset] ?? CTA_FONT_PRESETS[CTA_DEFAULT_FONT_PRESET] ?? [32, 4, 64]
	const [fontMin, fontPreferred, fontMax] = fontSizes

	// Check if this is an internal media library image
	const isMediaImage = displayImageUrl.startsWith('media/')

	return (
		<section
			ref={sectionRef}
			className="relative w-full h-[580px] overflow-hidden bg-[#fcfbfe]"
		>
			{/* Person image - positioned behind the gradient overlay */}
			<motion.div
				className="absolute left-8 lg:left-16 bottom-0 z-0"
				initial={{ opacity: 0, x: -30 }}
				animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -30 }}
				transition={{ duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}
			>
				{isMediaImage ? (
					<Img
						src={`/resources/images?objectKey=${encodeURIComponent(displayImageUrl)}`}
						alt={displayImageAlt}
						width={400}
						height={520}
						className="h-[520px] w-auto object-cover object-top mb-0"
					/>
				) : (
					<img
						src={displayImageUrl}
						alt={displayImageAlt}
						className="h-[520px] w-auto object-cover object-top mb-0"
					/>
				)}
			</motion.div>

			{/* 20% transparent gradient overlay - on top of image */}
			<div
				className="absolute inset-0 z-10"
				style={{
					background:
						'linear-gradient(100deg, rgba(246, 146, 30, 0.2) 0%, rgba(246, 146, 30, 0.2) 65.64%, rgba(235, 0, 139, 0.2) 100%)',
				}}
			/>

			{/* Mobile-only bottom gradient for white text readability */}
			<div
				className="absolute inset-0 z-10 lg:hidden"
				style={{
					background:
						'linear-gradient(to top, rgba(0, 0, 0, 0.55) 0%, rgba(0, 0, 0, 0.25) 40%, transparent 65%)',
				}}
			/>

			{/* Wavy bottom edge - transition to footer */}
			<div className="absolute bottom-0 left-0 right-0 w-full translate-y-[99%] z-20">
				<svg
					viewBox="0 0 1440 120"
					preserveAspectRatio="none"
					className="w-full h-20 md:h-28 lg:h-32"
				>
					<path
						d="M0,0 C360,60 720,20 1080,40 C1260,50 1380,30 1440,20 L1440,0 Z"
						fill="#231a1a"
					/>
				</svg>
			</div>

			{/* Content container */}
			<div className="relative z-20 container mx-auto px-6 lg:px-12 h-full flex items-end pb-16 lg:items-center lg:pb-0">
				<div className="flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-16 w-full">
					{/* Left side - Spacer for image area */}
					<div className="hidden lg:block lg:w-[45%]" />

					{/* Right side - Text and CTA */}
					<motion.div
						className="w-full lg:w-[55%] text-center lg:text-left flex flex-col items-center lg:items-start justify-center lg:pl-12"
						initial={{ opacity: 0, y: 18 }}
						animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 18 }}
						transition={{ duration: 1, delay: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
					>
						<h2
							className="text-white lg:text-[#332f29] leading-[1.05] tracking-tight mb-6 lg:mb-12"
							style={{
								...CONDENSED_FONT_STYLE,
								fontSize: `clamp(${fontMin}px, ${fontPreferred}vw, ${fontMax}px)`,
							}}
						>
							{renderHighlightedTitle(displayTitle, highlightWords || undefined)}
						</h2>

						{displayShowButton && (
							<motion.div
								initial={{ opacity: 0, y: 12 }}
								animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 12 }}
								transition={{ duration: 0.8, delay: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
							>
								<Link to={displayButtonLink}>
									<InteractiveHoverButton text={displayButtonText} />
								</Link>
							</motion.div>
						)}
					</motion.div>
				</div>
			</div>
		</section>
	)
}
